import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, forwardRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import { Plus, ZoomIn, ZoomOut } from 'lucide-react';
import { toast } from '@/lib/toast';
import { blockNodeExtensions } from '@/editor/nodes';
import { blocksToTiptapDoc, tiptapDocToBlocks } from '@/editor/blockConversion';
import { sectionContentExtensions } from '@/editor/sectionContentExtensions';
import type { Block, Template } from '@/types';
import api from '@/lib/api';
import { useBlockStore } from '@/store/blockStore';
import { QuoteClientLogoProvider } from '@/store/quoteClientLogoContext';
import { useActiveEditor } from '@/store/editorContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LIBRARY_MENU_CONTENT_CLASS,
  LibraryPageButtons,
  LibraryPageMenuItems,
} from '@/components/library/LibraryPageMenuItems';
import { InvestmentAutoCalc } from '@/editor/extensions/investmentAutoCalc';
import { InvestmentMoneyBlur } from '@/editor/extensions/investmentMoneyBlur';
import { InvestmentRowDelete } from '@/editor/extensions/investmentRowDelete';
import { InvestmentRowLink } from '@/editor/extensions/investmentRowLink';
import { InvestmentRowVersion } from '@/editor/extensions/investmentRowVersion';
import { InvestmentCellShortcuts } from '@/editor/extensions/investmentCellShortcuts';
import { ScopedSelectAll } from '@/editor/extensions/scopedSelectAll';
import { InvestmentColumnResize } from '@/editor/extensions/investmentColumnResize';
import { InvestmentHiddenHours } from '@/editor/extensions/investmentHiddenHours';
import { InvestmentSelection } from '@/editor/extensions/investmentSelection';
import { QuotePageSelection, quotePageSelectionKey } from '@/editor/extensions/quotePageSelection';
import { QuotePageDrag } from '@/editor/extensions/quotePageDrag';
import { QuotePageCaret, caretInTextPage, isTextPage, selectionInPage } from '@/editor/extensions/quotePageCaret';
import { QuotePageInputGuard } from '@/editor/extensions/quotePageInputGuard';
import { PasteCleanup } from '@/editor/extensions/pasteCleanup';
import { A4_LANDSCAPE } from '@/constants/pageFormat';
import InvestmentRowInlineEditor from './InvestmentRowInlineEditor';
import QuoteDocument, { createQuoteRenderInput } from '@/components/quote-document/QuoteDocument';
import SheetGaps from '@/components/quote-document/SheetGaps';
import { SheetLayoutProvider } from '@/components/quote-document/SheetLayoutContext';
import { SheetPagination } from '@/components/quote-document/sheetPagination';
import { sheetStyles } from '@/components/quote-document/sheetStyles';
import { useSheetLayout } from '@/components/quote-document/useSheetLayout';
import { investmentDestinations, investmentGroupForPage, reorderQuotePages } from '@/editor/quoteWorkspace';

const SYNC_DEBOUNCE_MS = 600;
const PAGE_WIDTH = A4_LANDSCAPE.width;
const PAGE_HORIZONTAL_PADDING = 64;
const MIN_ZOOM = 0.4;
const MAX_ZOOM = 1;
const ZOOM_STEP = 0.05;

interface TiptapQuoteEditorProps {
  quoteId: string;
  blocks: Block[];
  onBlocksChange: (blocks: Block[]) => void;
  variablesMap?: Record<string, string>;
  /** UBX-preset + logo-fallbacks voor TipTap-secties (zelfde als blok-editor). */
  layoutConfig?: Record<string, unknown> | null;
  /** Sync paginaselectie in sidebar (alleen bij wijziging van actieve pagina-id). */
  onActiveBlockChange?: (blockId: string | null) => void;
  selectedBlockId?: string | null;
  sideBySide?: boolean;
  showSelection?: boolean;
}

export type TiptapQuoteEditorHandle = {
  selectBlock: (blockId: string) => void;
  reorderPages: (blockIds: string[]) => boolean;
  /** Kopie van een bibliotheekpagina, na `afterBlockId`. */
  addLibraryPage: (page: Template, afterBlockId: string) => Promise<void>;
  /** Directe sync van TipTap → blocks (bij tab sluiten / unmount). */
  flushSync: () => void;
  /** Annuleert geplande debounced sync (o.a. vóór sidebar-delete, undo/redo) zonder stale doc te pushen. */
  cancelPendingSync: () => void;
};

const TiptapQuoteEditor = forwardRef<TiptapQuoteEditorHandle, TiptapQuoteEditorProps>(
  function TiptapQuoteEditor(
    {
      quoteId,
      blocks,
      onBlocksChange,
      variablesMap,
      layoutConfig,
      onActiveBlockChange,
      selectedBlockId,
      sideBySide = false,
      showSelection = true,
    },
    ref
  ) {
    const { setActiveEditor, setDocumentEditor } = useActiveEditor();
    const [selectedBlock, setSelectedBlock] = useState<Block | null>(null);
    const onActiveBlockChangeRef = useRef(onActiveBlockChange);
    onActiveBlockChangeRef.current = onActiveBlockChange;
    /** `undefined` = nog geen notify; daarna alleen bij wijziging van id. */
    const lastNotifiedSidebarIdRef = useRef<string | null | undefined>(undefined);

    const syncActiveBlockToSidebar = useCallback((block: Block | null) => {
      setSelectedBlock(block);
      const id = block?.id ?? null;
      if (
        lastNotifiedSidebarIdRef.current !== undefined &&
        id === lastNotifiedSidebarIdRef.current
      ) {
        return;
      }
      lastNotifiedSidebarIdRef.current = id;
      onActiveBlockChangeRef.current?.(id);
    }, []);

    useEffect(() => {
      lastNotifiedSidebarIdRef.current = undefined;
    }, [quoteId]);

    const [isAdding, setIsAdding] = useState(false);
    const isAddingRef = useRef(false);
    const syncTimeoutRef = useRef<number | null>(null);
    const externalSyncFrameRef = useRef<number | null>(null);
    const externalSyncReleaseFrameRef = useRef<number | null>(null);
    const isExternalUpdateRef = useRef(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [zoom, setZoom] = useState(1);
    const [autoFitZoom, setAutoFitZoom] = useState(1);
    const [isManualZoom, setIsManualZoom] = useState(false);
    const [overflowingProducts, setOverflowingProducts] = useState(0);

    const blocksRef = useRef(blocks);
    blocksRef.current = blocks;
    const quoteIdRef = useRef(quoteId);
    quoteIdRef.current = quoteId;
    const onBlocksChangeRef = useRef(onBlocksChange);
    onBlocksChangeRef.current = onBlocksChange;

    useEffect(() => {
      const el = scrollContainerRef.current;
      if (!el) return;
      const compute = () => {
        const available = el.clientWidth - PAGE_HORIZONTAL_PADDING;
        const fit = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, available / PAGE_WIDTH));
        setAutoFitZoom(fit);
        if (!isManualZoom) setZoom(fit);
      };
      compute();
      const ro = new ResizeObserver(compute);
      ro.observe(el);
      return () => ro.disconnect();
    }, [isManualZoom]);

    // Houd het midden van het beeld vast als de zoom verandert, bijvoorbeeld
    // wanneer het bewerkpaneel in- of uitklapt en de preview breder wordt. De
    // positie komt uit het laatste scroll-event: bij uitzoomen kapt de browser
    // scrollTop al af voordat dit effect draait.
    const previousZoomRef = useRef(zoom);
    const scrollPositionRef = useRef({ top: 0, height: 0 });
    useEffect(() => {
      const el = scrollContainerRef.current;
      if (!el) return;
      const remember = () => {
        scrollPositionRef.current = { top: el.scrollTop, height: el.clientHeight };
      };
      remember();
      el.addEventListener('scroll', remember, { passive: true });
      return () => el.removeEventListener('scroll', remember);
    }, []);
    useLayoutEffect(() => {
      const el = scrollContainerRef.current;
      const previous = previousZoomRef.current;
      previousZoomRef.current = zoom;
      if (!el || previous === zoom) return;
      const { top, height } = scrollPositionRef.current;
      el.scrollTop = (top + height / 2) * (zoom / previous) - el.clientHeight / 2;
    }, [zoom]);

    const zoomIn = useCallback(() => {
      setIsManualZoom(true);
      setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)));
    }, []);
    const zoomOut = useCallback(() => {
      setIsManualZoom(true);
      setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)));
    }, []);
    const zoomReset = useCallback(() => {
      setIsManualZoom(false);
      setZoom(autoFitZoom);
    }, [autoFitZoom]);

    const PAGE_BREAK_BG = '#f0f0f0';
    const editor = useEditor({
      extensions: [
        ...sectionContentExtensions,
        InvestmentColumnResize,
        InvestmentRowDelete,
        InvestmentRowLink,
        InvestmentRowVersion,
        InvestmentCellShortcuts,
        ScopedSelectAll,
        InvestmentAutoCalc,
        InvestmentMoneyBlur,
        InvestmentHiddenHours,
        InvestmentSelection,
        QuotePageSelection,
        QuotePageDrag,
        QuotePageCaret,
        QuotePageInputGuard,
        PasteCleanup,
        ...blockNodeExtensions,
        SheetPagination,
      ],
      content: blocksToTiptapDoc(blocks),
      editorProps: {
        attributes: {
          class: 'tiptap-editor max-w-none focus:outline-none',
        },
        handleClickOn: (view, _pos, node, _nodePos, event) => {
          if (view.dom.dataset.quoteWorkspace === 'preview' &&
              event.target instanceof Element && event.target.closest('.investment-main-header') &&
              !event.target.closest('input, textarea')) {
            const blockId = event.target.closest<HTMLElement>('.investment-group-view')?.dataset.blockId;
            const group = investmentGroupForPage(view.state.doc, blockId ?? null);
            if (group) {
              event.preventDefault();
              view.dom.dataset.workspaceSelection = 'active';
              view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, group.pos)));
              return true;
            }
          }
          if (node.attrs?.id) {
            const block = blocksRef.current.find((b) => b.id === node.attrs.id);
            if (block) syncActiveBlockToSidebar(block);
          }
          return false;
        },
      },
      onUpdate: ({ editor }) => {
        if (isExternalUpdateRef.current) return;
        if (syncTimeoutRef.current) window.clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = window.setTimeout(() => {
          syncTimeoutRef.current = null;
          const doc = editor.getJSON();
          const newBlocks = tiptapDocToBlocks(doc, quoteIdRef.current, blocksRef.current);
          // Voorkom volledige doc-replacement na interne sync (corrumpeert ProseMirror history).
          prevBlockIdsRef.current = newBlocks.map((b) => b.id).join(',');
          onBlocksChangeRef.current(newBlocks);
        }, SYNC_DEBOUNCE_MS);
      },
      onTransaction: ({ editor }) => {
        const pageId = quotePageSelectionKey.getState(editor.state);
        syncActiveBlockToSidebar(blocksRef.current.find((block) => block.id === pageId) ?? null);
      },
      onFocus: ({ editor: ed }) => {
        ed.view.dom.dataset.workspaceSelection = 'active';
        setActiveEditor(ed);
      },
    });

    useEffect(() => {
      if (!editor || editor.isDestroyed) return;
      editor.view.dom.dataset.quoteWorkspace = sideBySide ? 'preview' : 'document';
      editor.setEditable(true, false);
      editor.view.dispatch(editor.state.tr.setMeta('workspaceModeChanged', true));
    }, [editor, sideBySide]);

    // Dezelfde A4-vellen als de klantlink en de PDF (zie paginateDocument).
    const { layout: sheetLayout } = useSheetLayout(editor, 'web');
    const physicalPageCount = sheetLayout?.sheetCount || blocks.length;

    useEffect(() => {
      if (!editor || editor.isDestroyed) return;
      const root = editor.view.dom;
      let frameA = 0;
      let frameB = 0;
      const measure = () => {
        cancelAnimationFrame(frameA);
        cancelAnimationFrame(frameB);
        frameA = requestAnimationFrame(() => {
          frameB = requestAnimationFrame(() => {
            if (editor.isDestroyed) return;
            setOverflowingProducts(root.querySelectorAll('[data-product-overflow=true]').length);
          });
        });
      };
      const mutationObserver = new MutationObserver(measure);
      mutationObserver.observe(root, { childList: true, subtree: true, attributes: true });
      const resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(root);
      editor.on('transaction', measure);
      void document.fonts.ready.then(measure);
      measure();
      return () => {
        mutationObserver.disconnect();
        resizeObserver.disconnect();
        editor.off('transaction', measure);
        cancelAnimationFrame(frameA);
        cancelAnimationFrame(frameB);
      };
    }, [editor]);

    useEffect(() => {
      if (editor) {
        setDocumentEditor(editor);
        setActiveEditor(editor);
      }
      return () => {
        setDocumentEditor(null);
        setActiveEditor(null);
      };
    }, [editor, setActiveEditor, setDocumentEditor]);

    useEffect(() => {
      return () => {
        if (syncTimeoutRef.current) {
          window.clearTimeout(syncTimeoutRef.current);
          syncTimeoutRef.current = null;
        }
        // Geen flushSync bij unmount: kan corrupte/stale doc-state persisteren;
        // blockStore bevat de laatst succesvol gesynchroniseerde blocks (incl. visibility/pagehide flush).
      };
    }, []);

    useEffect(() => {
      if (!editor) return;
      const cancelPendingSync = () => {
        if (syncTimeoutRef.current) {
          window.clearTimeout(syncTimeoutRef.current);
          syncTimeoutRef.current = null;
        }
      };

      const handleKeyDown = (e: KeyboardEvent) => {
        // TipTap kan de sneltoets al hebben verwerkt vóór deze window-handler.
        if (e.defaultPrevented) return;
        const key = e.key.toLowerCase();

        const isUndoCombo = (e.metaKey || e.ctrlKey) && key === 'z';
        const isWindowsRedo = e.ctrlKey && !e.metaKey && key === 'y';

        if (!isUndoCombo && !isWindowsRedo) return;

        const active = document.activeElement;
        if (!active) return;

        const tag = (active as HTMLElement).tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;

        const rootDom = editor.view.dom;
        const nestedProse = active.closest('.ProseMirror');
        if (nestedProse && nestedProse !== rootDom && rootDom.contains(nestedProse)) {
          return;
        }

        const editorEl = editor.view.dom;
        const isInEditor = editorEl.contains(active) || editorEl === active;
        const isInEditorContainer = active?.closest('.editor-page-container') != null;
        if (!isInEditor && !isInEditorContainer) return;

        e.preventDefault();
        cancelPendingSync();

        if (isWindowsRedo) {
          if (!editor.can().redo()) {
            void useBlockStore.getState().redoBlocks();
          } else {
            editor.chain().focus().redo().run();
          }
          return;
        }

        if (isUndoCombo && e.shiftKey) {
          if (!editor.can().redo()) {
            void useBlockStore.getState().redoBlocks();
          } else {
            editor.chain().focus().redo().run();
          }
          return;
        }

        if (isUndoCombo) {
          if (!editor.can().undo()) {
            void useBlockStore.getState().undoBlocks();
          } else {
            editor.chain().focus().undo().run();
          }
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [editor]);

    useEffect(() => {
      if (editor && variablesMap) {
        editor.commands.setVariablesMap(variablesMap);
      }
    }, [editor, variablesMap]);

    // `useEditor` start al met deze blocks; voorkom daarom een redundante volledige
    // doc-replacement direct na mount (met name zichtbaar bij een nieuwe offerte).
    const prevBlockIdsRef = useRef(blocks.map((b) => b.id).join(','));
    const syncedQuoteIdRef = useRef(quoteId);
    // Blokwijzigingen van buiten de editor (autofill, AI) wijzigen de inhoud zonder dat de
    // block-id-lijst verandert. De store verhoogt dan externalBlocksRevision; alleen die
    // wijzigingen forceren hier een doc-sync. Editor-eigen updates (onBlocksChange) bumpen de
    // teller niet, zodat er geen feedback-loop/doc-replace per keystroke ontstaat.
    const externalBlocksRevision = useBlockStore((s) => s.externalBlocksRevision);
    const prevExternalRevisionRef = useRef(externalBlocksRevision);
    useEffect(() => {
      if (!editor) return;
      const blockIds = blocks.map((b) => b.id).join(',');
      const quoteChanged = quoteId !== syncedQuoteIdRef.current;
      const externalChanged = externalBlocksRevision !== prevExternalRevisionRef.current;
      if (!quoteChanged && blockIds === prevBlockIdsRef.current && !externalChanged) return;

      if (syncTimeoutRef.current) {
        window.clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }

      const doc = blocksToTiptapDoc(blocks);

      // Pages/React-nodeviews kunnen tijdens de commit zelf ook editor-transacties
      // uitvoeren. Een vooraf opgebouwde `editor.state.tr` is dan bij dispatch al
      // verouderd ("Applying a mismatched transaction"). Plan de vervanging na de
      // commit en laat TipTap de transactie vanuit de dan actuele state opbouwen.
      externalSyncFrameRef.current = requestAnimationFrame(() => {
        externalSyncFrameRef.current = null;
        if (editor.isDestroyed) return;

        isExternalUpdateRef.current = true;
        try {
          const applied = editor
            .chain()
            .setMeta('addToHistory', false)
            .setContent(doc, { emitUpdate: false })
            .run();
          if (applied) {
            prevBlockIdsRef.current = blockIds;
            syncedQuoteIdRef.current = quoteId;
            prevExternalRevisionRef.current = externalBlocksRevision;
          }
        } finally {
          externalSyncReleaseFrameRef.current = requestAnimationFrame(() => {
            externalSyncReleaseFrameRef.current = null;
            isExternalUpdateRef.current = false;
          });
        }
      });

      return () => {
        if (externalSyncFrameRef.current !== null) {
          cancelAnimationFrame(externalSyncFrameRef.current);
          externalSyncFrameRef.current = null;
        }
        if (externalSyncReleaseFrameRef.current !== null) {
          cancelAnimationFrame(externalSyncReleaseFrameRef.current);
          externalSyncReleaseFrameRef.current = null;
        }
        isExternalUpdateRef.current = false;
      };
    }, [blocks, quoteId, externalBlocksRevision, editor]);

    /** Flush eventuele pending sync zodat de structurele undo-snapshot actueel is. */
    const flushPendingSync = useCallback(() => {
      if (!editor || !syncTimeoutRef.current) return;
      window.clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
      const synced = tiptapDocToBlocks(editor.getJSON(), quoteIdRef.current, blocksRef.current);
      prevBlockIdsRef.current = synced.map((b) => b.id).join(',');
      onBlocksChangeRef.current(synced);
    }, [editor]);

    /**
     * Zet nieuwe pagina's na `insertAfterBlockId` (of achteraan). `createPages` maakt ze
     * op de server aan op de positie die hij meekrijgt; daarna komen ze in de editor.
     */
    const insertPages = useCallback(
      async (
        createPages: (insertAt: number) => Promise<Block[]>,
        label: string,
        insertAfterBlockId?: string | null
      ) => {
        if (!editor || isAddingRef.current) return;
        isAddingRef.current = true;
        setIsAdding(true);

        try {
          flushPendingSync();

          const currentBlocks = tiptapDocToBlocks(editor.getJSON(), quoteId, blocksRef.current);
          const afterIndex = currentBlocks.findIndex((b) => b.id === insertAfterBlockId);
          const insertAt = afterIndex >= 0 ? afterIndex + 1 : currentBlocks.length;
          const newBlocks = await createPages(insertAt);
          const firstBlock = newBlocks[0];

          const nodeContent = blocksToTiptapDoc(newBlocks).content ?? [];
          if (!firstBlock || nodeContent.length === 0) {
            toast.error('Kon pagina-inhoud niet genereren');
            return;
          }

          let inserted = false;

          if (insertAfterBlockId) {
            let insertPos: number | null = null;
            editor.state.doc.forEach((node, pos) => {
              if (node.attrs?.id === insertAfterBlockId) insertPos = pos + node.nodeSize;
            });
            if (insertPos != null) {
              // addToHistory: false — paginatoevoeging wordt via de structurele
              // blockStore-undo teruggedraaid, niet via Tiptap-history (die zou de
              // node alleen lokaal verwijderen zonder server-delete).
              inserted = editor
                .chain()
                .focus()
                .setMeta('addToHistory', false)
                .insertContentAt(insertPos, nodeContent)
                .run();
            }
          }

          if (!inserted) {
            const endPos = editor.state.doc.content.size;
            inserted = editor
              .chain()
              .focus()
              .setMeta('addToHistory', false)
              .insertContentAt(endPos, nodeContent)
              .run();
          }

          if (!inserted) {
            toast.error('Kon pagina niet invoegen in de editor');
            return;
          }

          // Structurele snapshot vóór de store-sync, zodat ⌘Z de toegevoegde pagina
          // via undoBlocks kan terugdraaien (zelfde patroon als de AI-handlers).
          useBlockStore.getState().pushStructuralSnapshot();

          const synced = tiptapDocToBlocks(editor.getJSON(), quoteId, blocksRef.current);
          prevBlockIdsRef.current = synced.map((b) => b.id).join(',');
          onBlocksChangeRef.current(synced);
          syncActiveBlockToSidebar(firstBlock);
          requestAnimationFrame(() => {
            editor.view.dom
              .querySelector(`[data-block-id="${firstBlock.id}"]`)
              ?.scrollIntoView({ behavior: 'instant', block: 'center' });
          });
          await api.reorderBlocks(
            quoteId,
            synced.map((b) => b.id)
          );
          toast.success(`${label} toegevoegd`);
        } catch (err) {
          console.error('Failed to add block:', err);
          toast.error('Pagina toevoegen mislukt', { error: err });
        } finally {
          isAddingRef.current = false;
          setIsAdding(false);
        }
      },
      [editor, quoteId, syncActiveBlockToSidebar, flushPendingSync]
    );

    /** Een kopie: wat je hier aanpast, verandert de bibliotheekpagina niet (en andersom). */
    const handleAddLibraryPage = useCallback(
      (page: Template, insertAfterBlockId?: string | null) =>
        insertPages(
          (insertAt) => api.insertTemplatePages(page.id, quoteId, insertAt),
          `‘${page.name}’`,
          insertAfterBlockId
        ),
      [insertPages, quoteId]
    );

    useImperativeHandle(
      ref,
      () => ({
        reorderPages: (blockIds) => {
          if (!editor) return false;
          setActiveEditor(editor);
          return reorderQuotePages(editor, blockIds);
        },
        selectBlock: (blockId) => {
          if (!editor) return;
          setActiveEditor(editor);
          let position: number | null = null;
          editor.state.doc.forEach((node, offset) => {
            if (node.attrs.id === blockId) position = offset;
          });
          if (position !== null) {
            const investmentOverview = sideBySide &&
              editor.state.doc.nodeAt(position)?.type.name === 'investmentGroupNode';
            editor.view.dom.dataset.workspaceSelection = 'active';
            editor.view.dispatch(
              editor.state.tr.setSelection(
                investmentOverview || editor.state.doc.nodeAt(position)?.isAtom
                  ? NodeSelection.create(editor.state.doc, position)
                  : TextSelection.near(editor.state.doc.resolve(position + 1))
              )
            );
          }
          const block = blocksRef.current.find((entry) => entry.id === blockId);
          if (block) syncActiveBlockToSidebar(block);
        },
        flushSync: () => {
          if (syncTimeoutRef.current) {
            window.clearTimeout(syncTimeoutRef.current);
            syncTimeoutRef.current = null;
          }
          if (!editor) return;
          const newBlocks = tiptapDocToBlocks(
            editor.getJSON(),
            quoteIdRef.current,
            blocksRef.current
          );
          prevBlockIdsRef.current = newBlocks.map((b) => b.id).join(',');
          onBlocksChangeRef.current(newBlocks);
        },
        cancelPendingSync: () => {
          if (syncTimeoutRef.current) {
            window.clearTimeout(syncTimeoutRef.current);
            syncTimeoutRef.current = null;
          }
        },
        addLibraryPage: (page, afterBlockId) => handleAddLibraryPage(page, afterBlockId),
      }),
      [editor, handleAddLibraryPage, sideBySide, syncActiveBlockToSidebar, setActiveEditor]
    );

    const [gapMenu, setGapMenu] = useState<{
      x: number;
      y: number;
      afterBlockId: string | null;
    } | null>(null);

    /** De ruimte tussen twee pagina's (SheetGaps) voegt daar een pagina in. */
    const handleGapClick = useCallback((e: React.MouseEvent) => {
      const gap = (e.target as HTMLElement).closest<HTMLElement>('.sheet-insert-gap');
      if (!gap) return;
      e.stopPropagation();
      e.preventDefault();
      const gapRect = gap.getBoundingClientRect();
      setGapMenu({
        x: gapRect.left + gapRect.width / 2,
        y: gapRect.top + gapRect.height / 2,
        afterBlockId: gap.dataset.afterBlockId ?? null,
      });
    }, []);

    if (!editor) return null;

    const renderInput = createQuoteRenderInput({
      blocks,
      layoutConfig: layoutConfig ?? {},
      variableMap: variablesMap ?? {},
      publicLocale: 'nl',
    });

    return (
      <QuoteClientLogoProvider blocks={blocks} layoutConfig={layoutConfig}>
        <QuoteDocument mode="edit" input={renderInput}>
          <div className="flex flex-1 flex-col overflow-hidden min-w-0">
            <div
              ref={scrollContainerRef}
              className="quote-preview-scroll flex-1 min-h-0 overflow-auto"
              data-quote-preview-scroll
              data-hide-selection={showSelection ? undefined : 'true'}
              style={
                {
                  backgroundColor: PAGE_BREAK_BG,
                  '--quote-sheet-gap-color': PAGE_BREAK_BG,
                } as React.CSSProperties
              }
              onClick={handleGapClick}
              onFocusCapture={(event) => {
                if (!editor) return;
                const target = event.target;
                if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
                if (!editor.view.dom.contains(target) || target.closest('[data-product-group]')) return;
                setActiveEditor(editor);
                const blockId = target.closest<HTMLElement>('[data-block-id]')?.dataset.blockId;
                const tableId = target.closest<HTMLElement>('[data-investment-table-id]')?.dataset.investmentTableId;
                let pos: number | undefined;
                if (tableId) {
                  pos = investmentDestinations(editor.state.doc).find((phase) => phase.tableId === tableId)?.headerPos;
                } else {
                  editor.state.doc.forEach((node, offset) => {
                    if (node.attrs.id === blockId) pos = offset;
                  });
                }
                if (pos === undefined) return;
                editor.view.dom.dataset.workspaceSelection = 'active';
                const selection = NodeSelection.create(editor.state.doc, pos);
                editor.view.dispatch(editor.state.tr.setSelection(selection).setMeta(quotePageSelectionKey, blockId ?? null));
              }}
              onPointerDownCapture={(event) => {
                if (!editor || !(event.target instanceof Element)) return;
                const target = event.target;
                const blockId = target.closest<HTMLElement>('[data-block-id]')?.dataset.blockId;
                if (!blockId || !editor.view.dom.contains(target)) return;
                let pos: number | undefined;
                editor.state.doc.forEach((node, offset) => {
                  if (node.attrs.id === blockId) pos = offset;
                });
                if (pos === undefined) return;
                setActiveEditor(editor);
                editor.view.dom.dataset.workspaceSelection = 'active';
                const node = editor.state.doc.nodeAt(pos);
                const nativeContent = target.closest('[data-node-view-content]');
                const investmentPart = target.closest('[data-product-group], .investment-phase-header, .investment-total-node-view, .tableWrapper, table, .investment-rate-card');
                const tr = editor.state.tr.setMeta(quotePageSelectionKey, blockId);
                if (node && isTextPage(node)) {
                  // Een tekstpagina nooit als geheel: de eerste toets zou de hele pagina vervangen.
                  // Naast de tekst klikken zet de cursor in de pagina (QuotePageCaret verfijnt dat).
                  const caret = nativeContent ? null : caretInTextPage(editor.state.doc, pos);
                  if (caret && !selectionInPage(editor.state.selection, pos)) tr.setSelection(caret);
                } else if (node?.isAtom || (!nativeContent && !investmentPart)) {
                  tr.setSelection(NodeSelection.create(editor.state.doc, pos));
                }
                if (target.closest('.investment-main-header') && !target.closest('button, input, textarea, select, a')) event.preventDefault();
                editor.view.dispatch(tr);
              }}
            >
              <div style={{ zoom }} data-editor-zoom={zoom}>
                <div
                  className="editor-page-container"
                  data-sheet-mode="web"
                  style={{ width: PAGE_WIDTH, margin: '0 auto', paddingTop: 32, paddingBottom: 32 }}
                >
                  <style>{sheetStyles('.editor-page-container')}</style>
                  <div style={{ position: 'relative' }}>
                    <SheetLayoutProvider value={sheetLayout}>
                      <EditorContent editor={editor} />
                    </SheetLayoutProvider>
                    {sheetLayout ? <SheetGaps layout={sheetLayout} insertGaps /> : null}
                  </div>
                </div>
              </div>
            </div>

            {!sideBySide && <InvestmentRowInlineEditor editor={editor} />}

            {/* Gap insert menu */}
            {gapMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setGapMenu(null)} />
                <div
                  className="fixed z-50 w-56 max-h-[60vh] overflow-y-auto rounded-md border border-border bg-white py-1 shadow-lg animate-in fade-in-0 zoom-in-95"
                  style={{ left: gapMenu.x, top: gapMenu.y, transform: 'translate(-50%, 4px)' }}
                >
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    Pagina invoegen
                  </div>
                  <LibraryPageButtons
                    onSelect={(page) => {
                      handleAddLibraryPage(page, gapMenu.afterBlockId);
                      setGapMenu(null);
                    }}
                  />
                </div>
              </>
            )}

            {/* Bottom bar: page count, zoom, add page */}
            <div className="shrink-0 flex items-center justify-between border-t border-border bg-white px-2 lg:px-4 py-1.5">
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {physicalPageCount} pagina{physicalPageCount !== 1 ? "'s" : ''}
              </span>
              {sideBySide &&
                (overflowingProducts ? (
                  <span role="alert" className="text-xs text-amber-800">
                    {overflowingProducts} productopbouw past mogelijk niet op één pagina. Kies Eén
                    regel of verkort de opbouw.
                  </span>
                ) : (
                  <span className="hidden xl:inline text-xs text-muted-foreground">
                    Preview en formulier scrollen onafhankelijk
                  </span>
                ))}

              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={zoomOut}
                  title="Uitzoomen"
                  disabled={zoom <= MIN_ZOOM}
                >
                  <ZoomOut className="h-3.5 w-3.5" />
                </Button>
                <button
                  onClick={zoomReset}
                  className="text-xs text-muted-foreground hover:text-foreground tabular-nums min-w-[3.5rem] text-center"
                  title={isManualZoom ? 'Passend maken' : 'Zoom'}
                >
                  {Math.round(zoom * 100)}%
                </button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={zoomIn}
                  title="Inzoomen"
                  disabled={zoom >= MAX_ZOOM}
                >
                  <ZoomIn className="h-3.5 w-3.5" />
                </Button>
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" className="gap-1.5" disabled={isAdding}>
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">Pagina toevoegen</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={cn('w-56', LIBRARY_MENU_CONTENT_CLASS)}>
                  <LibraryPageMenuItems
                    onSelect={(page) =>
                      handleAddLibraryPage(page, selectedBlockId ?? selectedBlock?.id ?? null)
                    }
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </QuoteDocument>
      </QuoteClientLogoProvider>
    );
  }
);

export default TiptapQuoteEditor;
